// Pure helpers for alert rules (NotificationPreference). For each flag the
// server applies the most specific matching rule that sets it (null defers):
// member and place, member with any place, any member at the place, then
// circle-wide. Low-battery, location-stale, location-off, speed and check-in alerts live on
// rules without a place, so for them it is the member's rule, then the
// circle-wide one. Unset is off, except for check-ins, which are on outside
// friends circles.
import type { Schemas } from "@10twenty/api";
import { speedText, type Units } from "../units";

type Preference = Schemas["NotificationPreference"];
type Flag = "on_arrive" | "on_leave" | "low_battery" | "location_stale" | "location_off" | "on_check_in";
export type MemberAlert = "low_battery" | "location_stale" | "location_off" | "on_check_in";
export type AlertMode = "off" | "arrive" | "leave" | "both";

export const ALERT_MODES: readonly AlertMode[] = ["off", "arrive", "leave", "both"];
export const MODE_LABELS: Record<AlertMode, string> = {
  off: "Off",
  arrive: "Arrivals",
  leave: "Departures",
  both: "Arrivals and departures",
};
export const MEMBER_ALERTS: readonly MemberAlert[] = ["low_battery", "location_stale", "location_off", "on_check_in"];
export const MEMBER_ALERT_LABELS: Record<MemberAlert, string> = {
  low_battery: "Low battery",
  location_stale: "No location for an hour",
  location_off: "Location turned off",
  on_check_in: "Check-ins",
};
/** What a member alert is when no rule sets it: a check-in is sent on purpose, so it is on. */
export const MEMBER_ALERT_DEFAULTS: Record<MemberAlert, boolean> = {
  low_battery: false,
  location_stale: false,
  location_off: false,
  on_check_in: true,
};

/** A member alert's value when no rule sets it, in a circle of `kind`: check-ins are off in friends circles. */
export function memberAlertDefault(alert: MemberAlert, kind: Schemas["CircleKind"]): boolean {
  return alert === "on_check_in" && kind === "friends" ? false : MEMBER_ALERT_DEFAULTS[alert];
}

/** The explicit mode of a rule, or null if it leaves arrivals and departures to other rules. */
export function modeOf(rule: Pick<Preference, "on_arrive" | "on_leave">): AlertMode | null {
  if (rule.on_arrive == null && rule.on_leave == null) return null;
  if (rule.on_arrive) return rule.on_leave ? "both" : "arrive";
  return rule.on_leave ? "leave" : "off";
}

function flagsOf(mode: AlertMode | null): Pick<Preference, "on_arrive" | "on_leave"> {
  if (mode === null) return { on_arrive: null, on_leave: null };
  return { on_arrive: mode === "arrive" || mode === "both", on_leave: mode === "leave" || mode === "both" };
}

/** The explicit rule for one cell, if any (null ids are wildcards). */
export function ruleFor(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
): Preference | undefined {
  return rules.find((r) => r.circle_id === circleId && r.member_id === memberId && r.place_id === placeId);
}

/** The value less specific rules give `flag` for a cell (what "Default" means); unset is off. */
function inheritedFlag(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
  flag: Flag,
): boolean {
  const fallbacks: [string | null, string | null][] = [];
  if (memberId !== null && placeId !== null) fallbacks.push([memberId, null], [null, placeId]);
  if (memberId !== null || placeId !== null) fallbacks.push([null, null]);
  for (const [member, place] of fallbacks) {
    const value = ruleFor(rules, circleId, member, place)?.[flag];
    if (value != null) return value;
  }
  return false;
}

/**
 * The mode a cell would get from less specific rules if it had no mode of its
 * own: what "Default" means in its menu. The circle-wide cell defaults to off.
 */
export function inheritedMode(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
): AlertMode {
  return modeOf({
    on_arrive: inheritedFlag(rules, circleId, memberId, placeId, "on_arrive"),
    on_leave: inheritedFlag(rules, circleId, memberId, placeId, "on_leave"),
  })!;
}

/**
 * A cell's own mode, with a flag its rule leaves unset taken from less
 * specific rules, or null if the rule sets neither (the cell inherits).
 */
export function cellMode(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
): AlertMode | null {
  const own = ruleFor(rules, circleId, memberId, placeId);
  if (!own || modeOf(own) === null) return null;
  const resolve = (flag: "on_arrive" | "on_leave") =>
    own[flag] ?? inheritedFlag(rules, circleId, memberId, placeId, flag);
  return modeOf({ on_arrive: resolve("on_arrive"), on_leave: resolve("on_leave") });
}

/** The mode that applies to `memberId` at `placeId`, as the server decides it. */
export function effectiveMode(
  rules: readonly Preference[],
  circleId: string,
  memberId: string,
  placeId: string,
): AlertMode {
  return cellMode(rules, circleId, memberId, placeId) ?? inheritedMode(rules, circleId, memberId, placeId);
}

/** A member alert's explicit value on a member (or Everyone) row, or null when it inherits. */
export function memberAlertOf(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  alert: MemberAlert,
): boolean | null {
  return ruleFor(rules, circleId, memberId, null)?.[alert] ?? null;
}

/** The member alert value "Default" means for a member: the Everyone row's, else the alert's default. */
export function inheritedMemberAlert(
  rules: readonly Preference[],
  circleId: string,
  alert: MemberAlert,
  kind: Schemas["CircleKind"] = "family",
): boolean {
  return memberAlertOf(rules, circleId, null, alert) ?? memberAlertDefault(alert, kind);
}

// Applies `flags` to one cell's rule, creating it with every other flag null
// (inherit), and drops a rule left with nothing set.
function withFlags(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
  flags: Partial<Pick<Preference, Flag | "speed_over_kph">>,
): Preference[] {
  const existing = ruleFor(rules, circleId, memberId, placeId);
  const rest = rules.filter((r) => r !== existing);
  const rule: Preference = {
    ...(existing ?? {
      circle_id: circleId,
      member_id: memberId,
      place_id: placeId,
      on_arrive: null,
      on_leave: null,
      low_battery: null,
      location_stale: null,
      location_off: null,
      speed_over_kph: null,
      on_check_in: null,
    }),
    ...flags,
  };
  const set = (
    ["on_arrive", "on_leave", "low_battery", "location_stale", "location_off", "speed_over_kph", "on_check_in"] as const
  ).some((flag) => rule[flag] != null);
  return set ? [...rest, rule] : rest;
}

/**
 * Returns every rule (all circles, for the replace-all PUT) with one cell set
 * to `mode`, or inheriting when `mode` is null. Other flags of an existing rule
 * are kept; the circle-wide cell stores off as unset.
 */
export function withCell(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  placeId: string | null,
  mode: AlertMode | null,
): Preference[] {
  const root = memberId === null && placeId === null;
  return withFlags(rules, circleId, memberId, placeId, flagsOf(root && mode === "off" ? null : mode));
}

/**
 * Every rule with one member alert set on a member (or Everyone) row, or
 * inheriting when `value` is null. Everyone stores the alert's default (in a
 * circle of `kind`) as unset.
 */
export function withMemberAlert(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  alert: MemberAlert,
  value: boolean | null,
  kind: Schemas["CircleKind"] = "family",
): Preference[] {
  const unset = memberId === null && value === memberAlertDefault(alert, kind);
  return withFlags(rules, circleId, memberId, null, { [alert]: unset ? null : value });
}

/** A member (or Everyone) row's own speed threshold in km/h (0 = off), or null when it inherits. */
export function speedOf(rules: readonly Preference[], circleId: string, memberId: string | null): number | null {
  return ruleFor(rules, circleId, memberId, null)?.speed_over_kph ?? null;
}

/** The threshold "Default" means for a member: the Everyone row's, else 0 (off). */
export function inheritedSpeed(rules: readonly Preference[], circleId: string): number {
  return speedOf(rules, circleId, null) ?? 0;
}

/**
 * Every rule with one row's speed threshold (km/h, 0 = off) set, or inheriting
 * when `kph` is null. Everyone stores off as unset.
 */
export function withSpeed(
  rules: readonly Preference[],
  circleId: string,
  memberId: string | null,
  kph: number | null,
): Preference[] {
  return withFlags(rules, circleId, memberId, null, { speed_over_kph: memberId === null && kph === 0 ? null : kph });
}

const KPH_PER_MPH = 1.609344;

/**
 * Speed thresholds to offer (km/h, as stored): round km/h, or round mph
 * converted, plus `current` when it is none of them (set elsewhere).
 */
export function speedChoices(units: Units, current: number | null = null): number[] {
  const choices =
    units === "metric"
      ? Array.from({ length: 10 }, (_, i) => 50 + i * 10)
      : Array.from({ length: 13 }, (_, i) => Math.round((30 + i * 5) * KPH_PER_MPH));
  if (current !== null && current > 0 && !choices.includes(current)) choices.push(current);
  return choices.sort((a, b) => a - b);
}

/** "Over 100 km/h" / "Over 60 mph", or "Off" for 0. */
export function speedLabel(kph: number, units: Units): string {
  return kph > 0 ? `Over ${speedText(kph / 3.6, units)}` : "Off";
}

/** Decodes a base64url VAPID key into the bytes `PushManager.subscribe` takes. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/").replace(/=+$/, "");
  const binary = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** The server registration for a browser subscription (`PushSubscription.toJSON()`). */
export function webPushRegistration(subscription: PushSubscriptionJSON): Schemas["WebPushRegistration"] | null {
  const { endpoint, keys } = subscription;
  if (!endpoint || !keys?.p256dh || !keys.auth) return null;
  return { type: "webpush", endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}
